import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { organizerDeleteEventApi, organizerListEventsApi, organizerUpdateEventApi } from "../../api/api";
import { MAIN_API } from "../../api/apilist";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import { handleError } from "../../helpers/handleError";
import "../dashboard.css";
import "./EventsList.css";
import { toDate } from "./dateUtils";

const SORT_OPTIONS = [
  { key: "name", label: "Nombre" },
  { key: "status", label: "Estado" },
  { key: "startDate", label: "Fecha de inicio" },
];

const EVENT_STATUS_TOKENS = ["active", "approved", "published", "activo", "en curso", "enabled"];
const EVENT_INACTIVE_TOKENS = ["inactive", "inactivo", "disabled", "draft", "archived", "paused"];

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
    <path d="m21 21-4.3-4.3" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12h14M12 5v14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CalendarSquareIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MetaCalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="2.75" y="3.75" width="18.5" height="17.5" rx="2.75" stroke="currentColor" strokeWidth="1.7" />
    <path d="M7.5 2.5v3.5M16.5 2.5v3.5M2.75 9.25h18.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

const ArrowRightSquareIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <polyline points="16 17 21 12 16 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PlayCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none" />
  </svg>
);

const CheckSquareIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M9 11l3 3L20 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LocationPinIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5Z" />
  </svg>
);

const MoreHorizontalIcon = () => (
  <svg viewBox="0 0 24 8" fill="currentColor" aria-hidden="true">
    <circle cx="3" cy="4" r="2" />
    <circle cx="12" cy="4" r="2" />
    <circle cx="21" cy="4" r="2" />
  </svg>
);

const EyeIcon = () => (
  <svg viewBox="0 0 24 18" fill="none" aria-hidden="true">
    <path d="M2 9s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 9 2 9Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="12" cy="9" r="3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ToggleIcon = ({ active }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="1" y="6" width="22" height="12" rx="6" stroke="currentColor" strokeWidth="1.7" />
    <circle cx={active ? "16" : "8"} cy="12" r="3" fill="currentColor" stroke="none" />
  </svg>
);

const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 20h9" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const getEventId = (event) => String(event?.id || event?._id || "");

const resolveStartDate = (event) => toDate(event?.startDatee || event?.startDate || event?.start);
const resolveEndDate = (event) => toDate(event?.endDatee || event?.endDate || event?.end);

const resolveEventLocation = (event) => {
  const city = event?.cityid?.name || event?.city || event?.cityName || "";
  const state = event?.stateid?.name || event?.state || event?.stateName || "";
  const fullAddress = event?.fulladdress || event?.venueAddress || event?.address || "";
  const compact = [city, state].filter(Boolean).join(" · ");
  return compact || fullAddress || "Ubicación no definida";
};

const resolveEventImage = (event) => {
  const candidate = [
    event?.image,
    event?.headerimage,
    event?.headerImage,
    event?.cover,
    event?.mobileimage,
    event?.mobileImage,
  ].find((value) => typeof value === "string" && value.trim());

  if (!candidate) return "";
  if (/^https?:\/\//i.test(candidate) || /^blob:/i.test(candidate)) return candidate;
  if (candidate.startsWith("/")) return `${MAIN_API}${candidate}`;
  return `${MAIN_API}/${candidate}`;
};

const resolveActiveFlag = (event) => {
  const candidates = [event?.status, event?.state, event?.eventStatus, event?.isActive, event?.active];
  for (const value of candidates) {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value !== 0;
    if (typeof value === "string" && value.trim()) {
      const normalized = value.trim().toLowerCase();
      if (EVENT_STATUS_TOKENS.some((token) => normalized.includes(token))) return true;
      if (EVENT_INACTIVE_TOKENS.some((token) => normalized.includes(token))) return false;
    }
  }
  return true;
};

const resolveTiming = (event) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = resolveStartDate(event);
  const end = resolveEndDate(event) || start;

  if (end && end.getTime() < today.getTime()) return "finished";
  if (start && start.getTime() > today.getTime()) return "upcoming";
  if (start && end && start.getTime() <= today.getTime() && end.getTime() >= today.getTime()) return "inProgress";
  return resolveActiveFlag(event) ? "inProgress" : "finished";
};

const formatCardDate = (value) => {
  if (!value) return "-";
  return value.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
};

const buildEventCard = (event) => {
  const id = getEventId(event);
  const isActive = resolveActiveFlag(event);
  const startDate = resolveStartDate(event);
  const endDate = resolveEndDate(event);

  return {
    raw: event,
    id,
    name: event?.name || event?.title || "Evento sin nombre",
    location: resolveEventLocation(event),
    image: resolveEventImage(event),
    isActive,
    timing: resolveTiming(event),
    startDate,
    endDate,
    startLabel: formatCardDate(startDate),
    endLabel: formatCardDate(endDate),
  };
};

const sortCards = (cards, sortKey) => {
  const next = [...cards];
  if (sortKey === "name") {
    return next.sort((a, b) => a.name.localeCompare(b.name, "es"));
  }
  if (sortKey === "status") {
    return next.sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.name.localeCompare(b.name, "es"));
  }
  if (sortKey === "startDate") {
    return next.sort((a, b) => {
      const aTime = a.startDate ? a.startDate.getTime() : Number.MAX_SAFE_INTEGER;
      const bTime = b.startDate ? b.startDate.getTime() : Number.MAX_SAFE_INTEGER;
      return aTime - bTime || a.name.localeCompare(b.name, "es");
    });
  }
  return next;
};

const EventsList = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("status");
  const [sortOpen, setSortOpen] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState("");
  const [actionEventId, setActionEventId] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await organizerListEventsApi();
      setList(Array.isArray(data?.info) ? data.info : []);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!event.target.closest("[data-events-sort-root]")) {
        setSortOpen(false);
      }
      if (!event.target.closest("[data-events-menu-root]")) {
        setMenuOpenId("");
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const onDelete = async (id) => {
    if (!id || !window.confirm("¿Eliminar este evento?")) return;
    try {
      setActionEventId(id);
      await organizerDeleteEventApi({ id });
      await load();
    } catch (error) {
      handleError(error);
    } finally {
      setActionEventId("");
      setMenuOpenId("");
    }
  };

  const onToggleStatus = async (eventCard) => {
    if (!eventCard?.id) return;
    try {
      setActionEventId(eventCard.id);
      const formData = new FormData();
      formData.append("id", eventCard.id);
      formData.append("status", String(!eventCard.isActive));
      await organizerUpdateEventApi(formData);
      await load();
    } catch (error) {
      handleError(error);
    } finally {
      setActionEventId("");
      setMenuOpenId("");
    }
  };

  const cards = useMemo(() => list.map(buildEventCard), [list]);

  const metrics = useMemo(
    () =>
      cards.reduce(
        (acc, card) => {
          acc.total += 1;
          if (card.timing === "upcoming") acc.upcoming += 1;
          if (card.timing === "inProgress") acc.inProgress += 1;
          if (card.timing === "finished") acc.finished += 1;
          return acc;
        },
        { total: 0, upcoming: 0, inProgress: 0, finished: 0 }
      ),
    [cards]
  );

  const filteredCards = useMemo(() => {
    const query = search.trim().toLowerCase();
    const visibleCards = query
      ? cards.filter((card) => `${card.name} ${card.location}`.toLowerCase().includes(query))
      : cards;

    return sortCards(visibleCards, sortKey);
  }, [cards, search, sortKey]);

  if (loading && list.length === 0) return <SpinLoading />;

  return (
    <div className="organizer-page organizer-events-redesign">
      <section className="organizer-section organizer-events organizer-events-redesign__content">
        <div className="organizer-events-redesign__toolbar">
          <label className="organizer-events-redesign__search" aria-label="Buscar eventos">
            <SearchIcon />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar"
            />
          </label>

          <div className="organizer-events-redesign__sort" data-events-sort-root>
            <button
              type="button"
              className={`organizer-events-redesign__sort-toggle ${sortOpen ? "is-open" : ""}`}
              onClick={() => {
                setSortOpen((prev) => !prev);
                setMenuOpenId("");
              }}
            >
              <span>Ordenar por</span>
              <ChevronDownIcon />
            </button>
            {sortOpen && (
              <div className="organizer-events-redesign__sort-menu">
                {SORT_OPTIONS.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    className={sortKey === option.key ? "is-active" : ""}
                    onClick={() => {
                      setSortKey(option.key);
                      setSortOpen(false);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            type="button"
            className="organizer-events-redesign__create"
            onClick={() => navigate("/organizer/events/new")}
          >
            <PlusIcon />
            <span>Crear evento</span>
          </button>
        </div>

        <div className="organizer-events-redesign__stats">
          <div className="organizer-events-redesign__stat">
            <span className="organizer-events-redesign__stat-icon">
              <CalendarSquareIcon />
            </span>
            <div>
              <div className="organizer-events-redesign__stat-label">TOTAL</div>
              <div className="organizer-events-redesign__stat-value">{metrics.total}</div>
            </div>
          </div>

          <div className="organizer-events-redesign__stat">
            <span className="organizer-events-redesign__stat-icon">
              <ArrowRightSquareIcon />
            </span>
            <div>
              <div className="organizer-events-redesign__stat-label">PRÓXIMOS</div>
              <div className="organizer-events-redesign__stat-value">{metrics.upcoming}</div>
            </div>
          </div>

          <div className="organizer-events-redesign__stat">
            <span className="organizer-events-redesign__stat-icon">
              <PlayCircleIcon />
            </span>
            <div>
              <div className="organizer-events-redesign__stat-label">EN CURSO</div>
              <div className="organizer-events-redesign__stat-value">{metrics.inProgress}</div>
            </div>
          </div>

          <div className="organizer-events-redesign__stat">
            <span className="organizer-events-redesign__stat-icon">
              <CheckSquareIcon />
            </span>
            <div>
              <div className="organizer-events-redesign__stat-label">FINALIZADOS</div>
              <div className="organizer-events-redesign__stat-value">{metrics.finished}</div>
            </div>
          </div>
        </div>

        {filteredCards.length > 0 ? (
          <div className="organizer-events-redesign__grid">
            {filteredCards.map((card) => {
              const isBusy = actionEventId === card.id;
              const isMenuOpen = menuOpenId === card.id;

              return (
                <article key={card.id} className="organizer-events-redesign__card">
                  <div className="organizer-events-redesign__card-media">
                    {card.image ? <img src={card.image} alt={card.name} loading="lazy" decoding="async" /> : null}
                  </div>

                  <div className="organizer-events-redesign__card-body">
                    <div className="organizer-events-redesign__card-top">
                      <div className="organizer-events-redesign__card-title-wrap">
                        <div className="organizer-events-redesign__card-title">{card.name}</div>
                        <div className="organizer-events-redesign__card-location">
                          <LocationPinIcon />
                          <span>{card.location}</span>
                        </div>
                      </div>

                      <div className="organizer-events-redesign__menu" data-events-menu-root>
                        <button
                          type="button"
                          className="organizer-events-redesign__menu-toggle"
                          onClick={() => {
                            setMenuOpenId((prev) => (prev === card.id ? "" : card.id));
                            setSortOpen(false);
                          }}
                          aria-label={`Abrir acciones para ${card.name}`}
                        >
                          <MoreHorizontalIcon />
                        </button>

                        {isMenuOpen && (
                          <div className="organizer-events-redesign__menu-panel">
                            <button
                              type="button"
                              className="organizer-events-redesign__menu-item"
                              onClick={() => window.open(`/event/${card.id}`, "_blank", "noopener,noreferrer")}
                            >
                              <EyeIcon />
                              <span>Ver Página Pública</span>
                            </button>

                            <button
                              type="button"
                              className="organizer-events-redesign__menu-item is-danger"
                              disabled={isBusy}
                              onClick={() => onDelete(card.id)}
                            >
                              <TrashIcon />
                              <span>Eliminar</span>
                            </button>

                            <button
                              type="button"
                              className="organizer-events-redesign__menu-item"
                              disabled={isBusy}
                              onClick={() => onToggleStatus(card)}
                            >
                              <ToggleIcon active={card.isActive} />
                              <span>{card.isActive ? "Desactivar" : "Activar"}</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="organizer-events-redesign__meta">
                      <div className="organizer-events-redesign__dates">
                        <div className="organizer-events-redesign__date">
                          <MetaCalendarIcon />
                          <div className="organizer-events-redesign__date-label">
                            <span className="organizer-events-redesign__date-caption">Inicio</span>
                            <span className="organizer-events-redesign__date-value">{card.startLabel}</span>
                          </div>
                        </div>

                        <div className="organizer-events-redesign__date">
                          <MetaCalendarIcon />
                          <div className="organizer-events-redesign__date-label">
                            <span className="organizer-events-redesign__date-caption">Fin</span>
                            <span className="organizer-events-redesign__date-value">{card.endLabel}</span>
                          </div>
                        </div>
                      </div>

                      <span className={`organizer-events-redesign__status ${card.isActive ? "is-active" : "is-inactive"}`}>
                        <i />
                        {card.isActive ? "Activo" : "Inactivo"}
                      </span>
                    </div>

                    <button
                      type="button"
                      className="organizer-events-redesign__card-action"
                      disabled={isBusy}
                      onClick={() => {
                        if (card.isActive) {
                          navigate(`/organizer/events/${card.id}`, { state: { event: card.raw } });
                          return;
                        }
                        onToggleStatus(card);
                      }}
                    >
                      {card.isActive ? <EditIcon /> : <PlayCircleIcon />}
                      <span>{isBusy ? "Procesando..." : card.isActive ? "Editar evento" : "Activar evento"}</span>
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="organizer-events-redesign__empty">
            <div className="organizer-events-redesign__empty-title">No encontramos eventos</div>
            <div className="organizer-events-redesign__empty-copy">
              {search.trim()
                ? "Probá con otro término de búsqueda o limpiá el filtro para ver todos tus eventos."
                : "Todavía no tenés eventos creados en este módulo."}
            </div>
            {!search.trim() && (
              <button
                type="button"
                className="organizer-events-redesign__empty-action"
                onClick={() => navigate("/organizer/events/new")}
              >
                Crear evento
              </button>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default EventsList;
